<script setup lang="ts">
import { computed, onMounted } from 'vue'
import {
  SITE_NAME_PRIMARY,
  SITE_NAME_MIDDLE,
  SITE_NAME_SUFFIX,
  SITE_NAME_EN,
} from '@/config/site'
import { usePlatformBrand } from '@/composables/usePlatformBrand'

const { platformName, logoUrl, initPlatformBrand } = usePlatformBrand()
onMounted(() => initPlatformBrand())

withDefaults(defineProps<{
  size?: number
  variant?: 'mark' | 'inline' | 'stacked'
  theme?: 'light' | 'dark'
  showEn?: boolean
  compact?: boolean
}>(), {
  size: 40,
  variant: 'inline',
  theme: 'light',
  showEn: false,
  compact: false,
})

const useCustomName = computed(() => platformName.value && platformName.value.trim().length > 0)
const useCustomLogo = computed(() => logoUrl.value && logoUrl.value.trim().length > 0)
const fullName = computed(() => `${SITE_NAME_PRIMARY}${SITE_NAME_MIDDLE}${SITE_NAME_SUFFIX}`)
</script>

<template>
  <div
    class="brand-logo"
    :class="[`brand-logo--${variant}`, `brand-logo--${theme}`, { 'brand-logo--compact': compact }]"
    :style="{ '--logo-size': `${size}px` }"
    :aria-label="useCustomName ? platformName : fullName"
  >
    <!-- 自定义 LOGO 图片（后端上传） -->
    <img
      v-if="useCustomLogo"
      class="brand-logo-mark brand-logo-img"
      :src="logoUrl"
      alt="logo"
    />
    <!-- 默认图形标：全球弧线 + 短信信号条 -->
    <svg
      v-else
      class="brand-logo-mark"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="2" y="2" width="44" height="44" rx="11" fill="url(#eis-bg)" />
      <rect x="2" y="2" width="44" height="44" rx="11" stroke="url(#eis-border)" stroke-width="1.4" />
      <ellipse cx="24" cy="24" rx="15" ry="15" stroke="#1e5a8a" stroke-width="1" opacity="0.55" />
      <ellipse cx="24" cy="24" rx="15" ry="5.5" stroke="#0891b2" stroke-width="1.2" opacity="0.85" />
      <path d="M24 9v30" stroke="#1e5a8a" stroke-width="1" opacity="0.45" />
      <path d="M11 24h26" stroke="#1e5a8a" stroke-width="1" opacity="0.35" />
      <rect x="17" y="27" width="3" height="5" rx="1.2" fill="#0891b2" opacity="0.75" />
      <rect x="22" y="24" width="3" height="8" rx="1.2" fill="#22d3ee" />
      <rect x="27" y="21" width="3" height="11" rx="1.2" fill="#67e8f9" />
      <path d="M30 18 L36 14 L36 22 Z" fill="#22d3ee" opacity="0.95" />
      <defs>
        <linearGradient id="eis-bg" x1="6" y1="4" x2="42" y2="44" gradientUnits="userSpaceOnUse">
          <stop stop-color="#0c2a4a" />
          <stop offset="1" stop-color="#061525" />
        </linearGradient>
        <linearGradient id="eis-border" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop stop-color="#1e4976" />
          <stop offset="0.45" stop-color="#0891b2" />
          <stop offset="1" stop-color="#1e4976" />
        </linearGradient>
      </defs>
    </svg>

    <div v-if="variant !== 'mark'" class="brand-logo-copy">
      <div v-if="useCustomName" class="brand-wordmark-zh">
        <span class="wm-primary">{{ platformName }}</span>
      </div>
      <div v-else class="brand-wordmark-zh">
        <span class="wm-primary">{{ SITE_NAME_PRIMARY }}</span>
        <span class="wm-middle">{{ SITE_NAME_MIDDLE }}</span>
        <span class="wm-suffix">{{ SITE_NAME_SUFFIX }}</span>
      </div>
      <div v-if="!useCustomName && (showEn || variant === 'stacked')" class="brand-wordmark-en">{{ SITE_NAME_EN }}</div>
    </div>
  </div>
</template>

<style scoped>
.brand-logo {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--logo-size) * 0.28);
  min-width: 0;
}

.brand-logo-mark {
  width: var(--logo-size);
  height: var(--logo-size);
  flex-shrink: 0;
}

.brand-logo-img {
  object-fit: contain;
  border-radius: calc(var(--logo-size) * 0.18);
}

.brand-logo--stacked {
  flex-direction: column;
  align-items: center;
  gap: calc(var(--logo-size) * 0.32);
  text-align: center;
}

.brand-logo-copy {
  display: flex;
  flex-direction: column;
  gap: calc(var(--logo-size) * 0.06);
  min-width: 0;
}

.brand-wordmark-zh {
  display: flex;
  align-items: baseline;
  flex-wrap: nowrap;
  line-height: 1.15;
  white-space: nowrap;
}

.wm-primary {
  font-size: calc(var(--logo-size) * 0.42);
  font-weight: 800;
  letter-spacing: 0.04em;
}

.wm-middle {
  font-size: calc(var(--logo-size) * 0.34);
  font-weight: 500;
  letter-spacing: 0.06em;
  margin-left: 0.04em;
}

.wm-suffix {
  font-size: calc(var(--logo-size) * 0.36);
  font-weight: 800;
  letter-spacing: 0.14em;
  margin-left: 0.12em;
}

.brand-wordmark-en {
  font-size: calc(var(--logo-size) * 0.155);
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  line-height: 1.2;
  white-space: nowrap;
}

.brand-logo--compact .brand-wordmark-en {
  display: none;
}

.brand-logo--compact .wm-middle {
  display: none;
}

/* light theme — sidebar */
.brand-logo--light .wm-primary,
.brand-logo--light .wm-middle {
  color: #0f172a;
}

.brand-logo--light .wm-suffix {
  color: #0891b2;
}

.brand-logo--light .brand-wordmark-en {
  color: #64748b;
}

/* dark theme — login */
.brand-logo--dark .wm-primary,
.brand-logo--dark .wm-middle {
  color: #f8fafc;
}

.brand-logo--dark .wm-suffix {
  color: #22d3ee;
}

.brand-logo--dark .brand-wordmark-en {
  color: #94a3b8;
}
</style>
